import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { useProfileSwitcher } from '@/hooks/useProfileSwitcher';
import { ClienteOption } from '@/components/ui/cliente-autocomplete';

export const useClienteAutocomplete = () => {
  const { profile } = useAuth();
  const { getCurrentProfile } = useProfileSwitcher();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCliente, setSelectedCliente] = useState<ClienteOption | null>(null);

  // Buscar clientes baseado no termo de pesquisa
  const { data: clientes, isLoading } = useQuery({
    queryKey: ['clientes-autocomplete', searchTerm, profile?.uuid],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2) return [];

      // Aplicar filtros baseados no usuário atual
      const currentRole = getCurrentProfile;
      let filterByUser: string | undefined;

      // Para líderes de vendas, buscar clientes criados pelos relacionadores da mesma equipe
      if (currentRole === 'LIDER_VENDAS') {
        try {
          // Buscar o supervisor do líder atual
          const { data: consultorAtual } = await supabase
            .from('consultores')
            .select('supervisor_id')
            .eq('user_id', profile?.uuid || '')
            .eq('tipo', 'lider')
            .maybeSingle();

          if (consultorAtual?.supervisor_id) {
            // Buscar todos os relacionadores supervisionados pelo mesmo supervisor
            const { data: relacionadores } = await supabase
              .from('consultores')
              .select('user_id')
              .eq('supervisor_id', consultorAtual.supervisor_id)
              .eq('tipo', 'relacionador')
              .eq('status', 'ativo');

            if (relacionadores && relacionadores.length > 0) {
              // Filtrar por múltiplos usuários (relacionadores da equipe + o próprio líder)
              const userIds = relacionadores.map(r => r.user_id);
              // Incluir o próprio líder de vendas na lista
              if (profile?.uuid && !userIds.includes(profile.uuid)) {
                userIds.push(profile.uuid);
              }
              filterByUser = userIds.join(',');
            } else {
              // Se não há relacionadores, mostrar apenas clientes do próprio líder
              filterByUser = profile?.uuid;
            }
          } else {
            // Fallback para o próprio usuário
            filterByUser = profile?.uuid;
          }
        } catch (error) {
          console.error('Erro ao buscar relacionadores da equipe:', error);
          filterByUser = profile?.uuid; // Fallback
        }
      } else if (currentRole === 'VENDEDOR') {
        // Vendedores comuns só veem seus próprios clientes
        filterByUser = profile?.uuid;
      }
      // Outros perfis (relacionadores, supervisores, etc.) veem todos os clientes

      // Buscar clientes por nome, CPF ou telefone
      // cpf/tel1 são armazenados sem formatação, então também buscamos pela versão só-dígitos do termo
      const termoSemVirgula = searchTerm.replace(/,/g, '');
      const somenteDigitos = termoSemVirgula.replace(/\D/g, '');
      const orConditions = [
        `nome.ilike.%${termoSemVirgula}%`,
        `cpf.ilike.%${termoSemVirgula}%`,
        `tel1.ilike.%${termoSemVirgula}%`,
      ];
      if (somenteDigitos && somenteDigitos !== termoSemVirgula) {
        orConditions.push(`cpf.ilike.%${somenteDigitos}%`, `tel1.ilike.%${somenteDigitos}%`);
      }

      let query = supabase
        .from('clientes')
        .select('id, nome, cpf, email, tel1, created_by')
        .or(orConditions.join(','))
        .limit(10);

      // Aplicar filtro de tenant se necessário
      if (profile?.tenant_id) {
        query = query.eq('tenant_id', profile.tenant_id);
      }

      // Aplicar filtro por usuário se definido
      if (filterByUser) {
        if (filterByUser.includes(',')) {
          const userIds = filterByUser.split(',').map(id => id.trim());
          query = query.in('created_by', userIds);
        } else {
          query = query.eq('created_by', filterByUser);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar clientes:', error);
        return [];
      }

      return data || [];
    },
    enabled: searchTerm.length >= 2,
    staleTime: 1000 * 60 * 5, // Cache por 5 minutos
  });

  const selectCliente = useCallback((cliente: ClienteOption) => {
    setSelectedCliente(cliente);
    setSearchTerm(cliente.nome);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCliente(null);
    setSearchTerm('');
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    clientes: clientes || [],
    isLoading,
    selectedCliente,
    setSelectedCliente,
    selectCliente,
    clearSelection,
  };
};
